import { ApiResponseError } from '@/lib/api/errors'
import { RestApiClient } from '@/lib/api/restClient'

export const API_AUDIENCE = 'https://api.justrelate.com'
const EXPIRY_SKEW_MS = 30_000

interface CachedToken {
  token: string
  expiresAt: number | null
}

const cache = new Map<string, CachedToken>()
const inflight = new Map<string, Promise<string>>()

function expiry(token: string): number | null {
  try {
    const part = token.split('.')[1]
    if (!part) return null
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(
      atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')),
    ) as { exp?: number }
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

function tokenCacheKey(instanceId: string, audience: string): string {
  return JSON.stringify([instanceId, audience])
}

async function loadToken(
  instanceId: string,
  audience: string,
): Promise<string> {
  const key = tokenCacheKey(instanceId, audience)
  const pending = inflight.get(key)
  if (pending) return pending

  const promise = new RestApiClient('/auth')
    .request<{ access_token: string }>(
      `/instance/${encodeURIComponent(instanceId)}/token?audience=${encodeURIComponent(audience)}`,
      { credentials: 'include' },
    )
    .then((response) => {
      if (typeof response.access_token !== 'string' || !response.access_token) {
        throw new Error('Invalid IAM token response')
      }
      cache.set(key, {
        token: response.access_token,
        expiresAt: expiry(response.access_token),
      })
      return response.access_token
    })
    .finally(() => inflight.delete(key))
  inflight.set(key, promise)
  return promise
}

export interface TokenProvider {
  get(): Promise<string>
  authorize<T>(request: (token: string) => Promise<T>): Promise<T>
}

export function createTokenProvider(
  instanceId: string,
  audience: string = API_AUDIENCE,
): TokenProvider {
  const key = tokenCacheKey(instanceId, audience)

  const getNativeToken = () => {
    const entry = cache.get(key)
    if (
      entry &&
      (entry.expiresAt === null ||
        entry.expiresAt > Date.now() + EXPIRY_SKEW_MS)
    ) {
      return Promise.resolve(entry.token)
    }
    return loadToken(instanceId, audience)
  }

  return {
    get: getNativeToken,
    async authorize<T>(request: (token: string) => Promise<T>): Promise<T> {
      const token = await getNativeToken()
      try {
        return await request(token)
      } catch (error) {
        if (error instanceof ApiResponseError && error.statusCode === 401) {
          cache.delete(key)
          return request(await loadToken(instanceId, audience))
        }
        throw error
      }
    },
  }
}
